import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  MessageSquare,
  Mail,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  MousePointer,
  Eye,
  DollarSign,
  RefreshCw
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function MessageLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/marketing/message-logs');
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load message logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(log => {
    const search = searchTerm.toLowerCase();
    return (
      log.customer_name?.toLowerCase().includes(search) ||
      log.customer_email?.toLowerCase().includes(search) ||
      log.campaign_name?.toLowerCase().includes(search) ||
      log.message_content?.toLowerCase().includes(search)
    );
  });

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <Mail className="w-4 h-4 text-blue-500" />;
      case 'whatsapp': return <MessageSquare className="w-4 h-4 text-green-500" />;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (log) => {
    if (log.booking_generated) {
      return <Badge className="bg-emerald-100 text-emerald-800">Converted</Badge>;
    }
    if (log.clicked) {
      return <Badge className="bg-orange-100 text-orange-800">Clicked</Badge>;
    }
    if (log.opened) {
      return <Badge className="bg-purple-100 text-purple-800">Opened</Badge>;
    }
    if (log.delivered) {
      return <Badge className="bg-green-100 text-green-800">Delivered</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800">Sent</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Message Logs
              </CardTitle>
              <CardDescription>View all sent marketing messages and their status</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <Button variant="outline" onClick={fetchLogs} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading message logs...</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No message logs found</p>
              <p className="text-sm text-gray-400">Messages will appear here after campaigns are sent</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-gray-600">Recipient</th>
                    <th className="pb-3 font-medium text-gray-600">Campaign</th>
                    <th className="pb-3 font-medium text-gray-600">Channel</th>
                    <th className="pb-3 font-medium text-gray-600">Status</th>
                    <th className="pb-3 font-medium text-gray-600">Sent At</th>
                    <th className="pb-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map(log => (
                    <tr key={log.id} className="border-b hover:bg-gray-50">
                      <td className="py-3">
                        <div>
                          <p className="font-medium text-gray-900">{log.customer_name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">
                            {log.channel === 'whatsapp' ? log.customer_phone : log.customer_email}
                          </p>
                        </div>
                      </td>
                      <td className="py-3">
                        <p className="text-gray-900">{log.campaign_name || '-'}</p>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          {getChannelIcon(log.channel)}
                          <span className="capitalize">{log.channel}</span>
                        </div>
                      </td>
                      <td className="py-3">
                        {getStatusBadge(log)}
                      </td>
                      <td className="py-3 text-sm text-gray-500">
                        {formatDate(log.sent_at)}
                      </td>
                      <td className="py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedLog(log); setDetailsOpen(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Message Details</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              {/* Recipient Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Recipient</p>
                  <p className="font-medium">{selectedLog.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Contact</p>
                  <p className="font-medium">
                    {selectedLog.channel === 'whatsapp' ? selectedLog.customer_phone : selectedLog.customer_email}
                  </p>
                </div>
              </div>

              {/* Campaign Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Campaign</p>
                  <p className="font-medium">{selectedLog.campaign_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Channel</p>
                  <div className="flex items-center gap-2">
                    {getChannelIcon(selectedLog.channel)}
                    <span className="capitalize font-medium">{selectedLog.channel}</span>
                  </div>
                </div>
              </div>

              {/* Subject (if email) */}
              {selectedLog.subject && (
                <div>
                  <p className="text-xs text-gray-500">Subject</p>
                  <p className="font-medium">{selectedLog.subject}</p>
                </div>
              )}

              {/* Message Content */}
              <div>
                <p className="text-xs text-gray-500">Message Content</p>
                <div className="bg-gray-50 rounded-lg p-3 mt-1 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {selectedLog.message_content}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Activity Timeline</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-blue-500" />
                    <span className="text-sm">Sent: {formatDate(selectedLog.sent_at)}</span>
                  </div>
                  {selectedLog.delivered && (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Delivered: {formatDate(selectedLog.delivered_at)}</span>
                    </div>
                  )}
                  {selectedLog.opened && (
                    <div className="flex items-center gap-3">
                      <Eye className="w-4 h-4 text-purple-500" />
                      <span className="text-sm">Opened: {formatDate(selectedLog.opened_at)}</span>
                    </div>
                  )}
                  {selectedLog.clicked && (
                    <div className="flex items-center gap-3">
                      <MousePointer className="w-4 h-4 text-orange-500" />
                      <span className="text-sm">Clicked: {formatDate(selectedLog.clicked_at)}</span>
                    </div>
                  )}
                  {selectedLog.booking_generated && (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm">Converted to Booking</span>
                      {selectedLog.revenue_attributed > 0 && (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          <DollarSign className="w-3 h-3 mr-1" />
                          ${selectedLog.revenue_attributed}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
